<script>
import { TabItemsDirective, TabItemDirective } from "@syncfusion/ej2-vue-navigations/src/tab/items.directive";
import moment from "moment";
import JsonViewer from 'vue-json-viewer';
import { defineAsyncComponent } from "@common/defineAsyncComponent";
// import { TabItemsDirective, TabItemDirective } from "@syncfusion/ej2-vue-navigations";
import settingsEditor from "@components/common/settingsEditor/settingsEditor";
import { componentStore } from "@common/componentStore/componentStore";
import { actionStore } from "@common/actionStore";
import { controlStore } from "@common/control/controlStore";
import { library } from "@common/library/library";
import { errorHandler } from "@common/errorHandler/errorHandler";
import SamTooltip from "@common/control/samTooltip";

const testLogic = [
    {
        record: 'self',
        valueType: 'path',
        valuePath: 'controls.makeFieldsRequiredLogic.value',
        criteriaRecord: 'component',
        prop: 'required',
        criteria: 'true'
    },
    {
        record: 'self',
        valueType: 'path',
        valuePath: 'controls.disableFieldsLogic.value',
        criteriaRecord: 'component',
        prop: 'disabled',
        criteria: 'true'
    },
    {
        record: 'self',
        valueType: 'value',
        value: 'A New Label',
        criteriaRecord: 'component',
        prop: 'label',
        criteria: 'this.controls.changeLabelsLogic.value === true'
    },
    {
        record: 'self',
        valueType: 'path',
        valuePath: 'controls.showFieldsLogic.value',
        criteriaRecord: 'component',
        prop: 'display',
        criteria: 'true'
    }
];

const getActionStoreRequiredParams = () => {
    let params = library.cloneObj(actionStore.state.requiredParams);
    let keys = Object.keys(params);
    let outputItems = [];
    keys.forEach((key) => {
        params[key].forEach((param) => {
            param.value = param.key;
            param.action = key;
            outputItems.push(param);
        });
    });
    // add openUrlParams here as none are required
    let openUrlParams = [
        {
            action: 'openUrl',
            value: 'url',
            key: 'url',
            text: 'Url to Open'
        }, {
            action: 'openUrl',
            value: 'urlParams',
            key: 'urlParams',
            text: 'URL Params'
        }, {
            action: 'openUrl',
            value: 'target',
            key: 'target',
            text: 'Target Window'
        }
    ];
    return [...outputItems, ...openUrlParams];
};

const editorSettings = {
    panel1: {
        type: 'panel',
        label: 'Basic Inputs',
        id: 'panel1',
        fields: [
            'someString',
            'someText',
            'someTextWithHTML',
            'someNumber',
            'numberMinMax',
            'someBoolean',
            'someDate',
            'someRelativeDate',
            'someTime',
            'someTimeNumber',
            'someIcon',
            'someColor',
            'someAttachment'
        ]
    },
    panel2: {
        type: 'panel',
        label: 'Checkboxes, Radios & Dropdowns',
        id: 'panel2',
        fields: [
            'someCheckbox',
            'someRadio',
            'someDropdown',
            'someDropdownSearch',
            'someDropdownSecondary',
            'someDropdownItemTemplate',
            'someDropdownMissingOptions',
            'someDropdownTree',
            'someDropdownTreeWithSearch',
            'someDropdownTreeSecondary',
            'someDropdownTreeItemTemplate',
            'someDropdownTreeMissingOptions',
            'someMultiSelect',
            'radioListWithCssClass',
            'checkboxListWithCssClass',
        ]
    },
    panel3: {
        type: 'panel',
        label: 'Dropdowns & Checkbox Lists using Items Source (itemsSource) Property',
        id: 'panel3',
        fields: [
            'allFieldsInput',
            'allAutomationsInput',
            'allBcCheckOptionsInput',
            'allSurveys',
            'allVols',
            'allOpps',
            'allOrgs',
            'dropdownWithAdditionalOptions',
            'asyncDropdownCheckbox',
            'asyncDropdownTree',
            'sampleDatasetId',
            'getDatasetDetails',
            'itemsSourceOptionsDropdown'
        ]
    },
    panel4: {
        type: 'panel',
        label: 'Sorted Dropdowns',
        id: 'panel4',
        fields: [
            'ascendingSortedDropdown',
            'descendingSortedDropdown',
            'numberSortedDropdown',
            'objectSortedDropdown',
            'sortedWithAbsoluteOption',
        ]
    },
    panel5: {
        type: 'panel',
        label: 'Objects & Arrays',
        id: 'panel5',
        fields: [
            'objectArray',
            'objectArrayPrimaryLabel',
            'arrayArray',
            'arrayArray2',
            'objectArrayAllExpanded',
            'someObject',
        ]
    },
    panel6: {
        type: 'panel',
        label: 'Action Field, Calc Field, Datasource Field',
        id: 'panel6',
        fields: [
            'configuredActions',
            'testCalc',
            'someDatasourceField'
        ]
    },
    panel7: {
        type: 'panel',
        label: 'Logic Editor',
        id: 'panel7',
        fields: [
            'someLogicField'
        ]
    },
    panel8: {
        type: 'panel',
        label: 'Multiple Column 2',
        id: 'panel8',
        fields: [
            'column1Field',
            'column2Field',
            'allVols'
        ]
    },
    panel9: {
        type: 'panel',
        label: 'Multiple Column 3',
        id: 'panel9',
        fields: [
            'column1Field',
            'column2Field',
            'column3Field'
        ]
    },
    someString: {
        label: 'Test Some String',
        type: 'string',
        id: 'someString',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someText: {
        label: 'Test Some Text',
        type: 'text',
        id: 'someText',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someTextWithHTML: {
        label: 'Test Some Text w/Rich Text Editor',
        type: 'text',
        ENABLE_HTML: true,
        id: 'someTextWithHTML',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someNumber: {
        label: 'Test Some Number',
        type: 'NUMBER',
        id: 'someNumber',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    numberMinMax: {
        label: 'Test Number Min Max',
        type: 'NUMBER',
        id: 'numberMinMax',
        helpText: 'Testing the message',
        min: 1,
        max: 100
        // logic: testLogic
    },
    someBoolean: {
        label: 'Test Some Boolean',
        type: 'BOOLEAN',
        id: 'someBoolean',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someDate: {
        label: 'Test Some Date',
        type: 'DATE',
        id: 'someDate',
        helpText: 'Testing the message',
    },
    someRelativeDate: {
        label: 'Test Some Relative Date',
        type: 'DATE',
        id: 'someRelativeDate',
        helpText: 'Testing the message',
        allowRelativeDate: true
        // logic: testLogic
    },
    someTime: {
        label: 'Test Some Time',
        type: 'time',
        id: 'someTime',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someTimeNumber: {
        label: 'Test Time as Number',
        type: 'time',
        valueAsNumber: true,
        id: 'someTimeNumber',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someIcon: {
        label: 'Test Some Icon',
        type: 'iconpicker',
        id: 'someIcon',
        helpText: 'Testing the message',
    },
    someColor: {
        label: 'Test Some Color',
        type: 'COLOR',
        id: 'someColor',
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someAttachment: {
        label: 'Test Some Attachment',
        type: 'upload',
        id: 'someAttachment',
        psp: 'component',
        helpText: 'Testing the message',
    },
    someCheckbox: {
        label: 'Test Some Checkbox',
        type: 'CHECKBOX_LIST',
        id: 'someCheckbox',
        items: [
            {
                label: 'A Value',
                value: 'a',
                sysVal: 'a'
            },
            {
                label: 'B Value',
                value: 'b',
                sysVal: 'b'
            },
            {
                label: 'C Value',
                value: 'c',
                sysVal: 'c'
            },
        ],
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someRadio: {
        label: 'Test Some Radio',
        type: 'RADIO_BUTTON_LIST',
        id: 'someRadio',
        items: [
            {
                label: 'A radio value',
                value: 'a radio value',
                sysVal: 'a radio value'
            },
            {
                label: 'B radio value',
                value: 'b radio value',
                sysVal: 'b radio value'
            },
        ],
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someDropdown: {
        label: 'Test Some Dropdown',
        type: 'dropdown',
        id: 'someDropdown',
        items: [
            'a dropdown value',
            'b dropdown value',
            'c dropdown value'
        ],
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someDropdownSearch: {
        label: 'Test Some Dropdown w/Search',
        type: 'dropdown',
        id: 'someDropdownSearch',
        items: [
            'dropdown value cat',
            'dropdown value dog',
            'dropdown value fish'
        ],
        allowFiltering: true,
        helpText: 'Testing the message',
    },
    someDropdownSecondary: {
        label: 'Test Some Dropdown w/Secondary Action',
        type: 'dropdown',
        name: 'someDropdownSecondary',
        helpText: 'Testing the message',
        cssClass: 'e-outline',
        secondaryActions: [{
            text: 'Secondary Action',
            action: 'testAction',
            source: 'actionStore',
            params: {
                param1: 'val2',
                param2: true,
                param3: 'Param c',
                param4: 444
            }
        }, {
            text: 'Secondary Action 2',
            action: 'testAction',
            source: 'actionStore',
            params: {
                param1: 'val3',
                param2: true,
                param3: 'Param d',
                param4: 555
            }
        }, {
            text: 'Secondary Action Chain',
            action: 'testAction',
            source: 'actionStore',
            params: {
                param1: 'val4',
                param2: true,
                param3: 'Param e',
                param4: 666
            },
            chain: [{
                action: 'testAction',
                source: 'actionStore',
                params: {
                    param1: 'val5',
                    param2: true,
                    param3: 'Chained Action',
                    param4: 777
                }
            }]
        }]
    },
    someDropdownItemTemplate: {
        label: 'Dropdown With Item Template',
        type: 'dropdown',
        id: 'someDropdownItemTemplate',
        fields: {
            text: 'text',
            value: 'value'
        },
        items: [
            {
                text: 'a dropdown value',
                value: 'a',
                prop3: 'some other prop a'
            },
            {
                text: 'b dropdown value',
                value: 'b',
                prop3: 'some other prop b'
            },
            {
                text: 'c dropdown value',
                value: 'c',
                prop3: 'some other prop c'
            }
        ],
        itemTemplate: `<span>an Item Template with <strong>HTML</strong></span><br/>Text is: {{data.text}} Value is: {{data.value}} prop3 is: {{data.prop3}}`,
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someDropdownMissingOptions: {
        label: 'Dropdown With Missing Options',
        type: 'dropdown',
        id: 'someDropdownMissingOptions',
        fields: {
            text: 'text',
            value: 'value'
        },
        items: [
            {
                text: 'a dropdown value',
                value: 'a',
                prop3: 'some other prop a'
            },
            {
                text: 'b dropdown value',
                value: 'b',
                prop3: 'some other prop b'
            },
            {
                text: 'c dropdown value',
                value: 'c',
                prop3: 'some other prop c'
            }
        ],
        helpText: 'How a dropdown control looks when the value does not match any options.',
        default: 'd'
    },
    someDropdownTree: {
        label: 'Test Some Dropdown Tree',
        type: 'dropdowntree',
        id: 'someDropdownTree',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        // logic: testLogic,
        items: [
            {
                value: '01',
                text: 'Music',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                child: [
                    {
                        value: '01-01',
                        text: 'Gouttes.mp3'
                    }
                ]
            },
            {
                value: '02',
                text: 'Videos',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                expanded: true,
                child: [
                    {
                        value: '02-01',
                        selectable: false,
                        htmlAttributes: { 'prevent-select': 'true' },
                        text: 'Naturals.mp4'
                    },
                    {
                        value: '02-02',
                        text: 'Wild.mpeg'
                    }
                ]
            }
        ],
        helpText: 'Testing the message'
    },
    someDropdownTreeWithSearch: {
        label: 'Test Some Dropdown Tree w/Search',
        type: 'dropdowntree',
        id: 'someDropdownTreeWithSearch',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        // logic: testLogic,
        items: [
            {
                value: '01',
                text: 'Music',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                child: [
                    {
                        value: '01-01',
                        text: 'Gouttes.mp3'
                    }
                ]
            },
            {
                value: '02',
                text: 'Videos',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                expanded: true,
                child: [
                    {
                        value: '02-01',
                        selectable: false,
                        htmlAttributes: { 'prevent-select': 'true' },
                        text: 'Naturals.mp4'
                    },
                    {
                        value: '02-02',
                        text: 'Wild.mpeg'
                    }
                ]
            }
        ],
        allowFiltering: true,
        helpText: 'Testing the message'
    },
    someDropdownTreeSecondary: {
        label: 'Test Some Dropdown Tree w/Secondary',
        type: 'dropdowntree',
        id: 'someDropdownTreeSecondary',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        // logic: testLogic,
        items: [
            {
                value: '01',
                text: 'Music',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                child: [
                    {
                        value: '01-01',
                        text: 'Gouttes.mp3'
                    }
                ]
            },
            {
                value: '02',
                text: 'Videos',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                expanded: true,
                child: [
                    {
                        value: '02-01',
                        selectable: false,
                        htmlAttributes: { 'prevent-select': 'true' },
                        text: 'Naturals.mp4'
                    },
                    {
                        value: '02-02',
                        text: 'Wild.mpeg'
                    }
                ]
            }
        ],
        secondaryActions: [{
            label: 'Secondary Action',
            action: 'testAction',
            source: 'actionStore',
            params: {
                param1: 'val2',
                param2: true,
                param3: 'Param c',
                param4: 444
            }
        }],
        helpText: 'Testing the message'
    },
    someDropdownTreeItemTemplate: {
        label: 'Dropdown Tree w/ Item Template',
        type: 'dropdowntree',
        id: 'someDropdownTreeItemTemplate',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        items: [
            {
                value: '01',
                text: 'Music',
                prop3: 'prop3 value',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                child: [
                    {
                        value: '01-01',
                        text: 'Gouttes.mp3',
                        prop3: 'test prop 3'
                    }
                ]
            },
            {
                value: '02',
                text: 'Videos',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                prop3: 'prop3 value',
                expanded: true,
                child: [
                    {
                        value: '02-01',
                        selectable: false,
                        htmlAttributes: { 'prevent-select': 'true' },
                        prop3: 'test prop 3',
                        text: 'Naturals.mp4'
                    },
                    {
                        value: '02-02',
                        prop3: 'test prop 3',
                        text: 'Wild.mpeg'
                    }
                ]
            }
        ],
        itemTemplate: `<span>an Item Template with <strong>HTML</strong></span><br/><span><strong>Text is:</strong> {{data.text}} <strong>Value is:</strong> {{data.value}} | <strong>prop3 is:</strong> {{data.prop3}} | <span v-if="data.child"> # of Children: {{data.child.length}}</span></span>`,
        helpText: 'Testing the message',
        // logic: testLogic
    },
    someDropdownTreeMissingOptions: {
        label: 'Dropdown Tree w/ Missing Options',
        type: 'dropdowntree',
        id: 'someDropdownTreeMissingOptions',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        items: [
            {
                value: '01',
                text: 'Music',
                prop3: 'prop3 value',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                child: [
                    {
                        value: '01-01',
                        text: 'Gouttes.mp3',
                        prop3: 'test prop 3'
                    }
                ]
            },
            {
                value: '02',
                text: 'Videos',
                selectable: false,
                htmlAttributes: { 'prevent-select': 'true' },
                prop3: 'prop3 value',
                expanded: true,
                child: [
                    {
                        value: '02-01',
                        selectable: false,
                        htmlAttributes: { 'prevent-select': 'true' },
                        prop3: 'test prop 3',
                        text: 'Naturals.mp4'
                    },
                    {
                        value: '02-02',
                        prop3: 'test prop 3',
                        text: 'Wild.mpeg'
                    }
                ]
            }
        ],
        itemTemplate: `<span>an Item Template with <strong>HTML</strong></span><br/><span><strong>Text is:</strong> {{data.text}} <strong>Value is:</strong> {{data.value}} | <strong>prop3 is:</strong> {{data.prop3}} | <span v-if="data.child"> # of Children: {{data.child.length}}</span></span>`,
        helpText: 'Testing the message',
        default: '001'
    },
    asyncDropdownTree: {
        label: 'Test Some Dropdown Tree w/Async Items',
        type: 'dropdowntree',
        id: 'asyncDropdownTree',
        fields: {
            value: 'value',
            text: 'text',
            child: 'child'
        },
        // logic: testLogic,
        itemsSource: 'this.testAsyncItems()',
        helpText: 'Testing the message'
    },
    asyncDropdownCheckbox: {
        label: 'Test Some Checkboxes w/Async Items',
        type: 'checkbox_list',
        id: 'asyncDropdownCheckbox',
        // logic: testLogic,
        itemsSource: 'configStore.testAsyncOptions()'
    },
    sampleDatasetId: {
        label: 'Sample Dataset',
        helpText: 'Sample Dataset for getDatasetDetails Control',
        type: 'dropdown',
        id: 'sampleDatsetId',
        items: ['1___0___Test For DEVD-813']
    },
    getDatasetDetails: {
        label: 'Test getDatasetDetails with 1 Dataset',
        type: 'dropdowntree',
        id: 'getDatasetDetails',
        fields: {
            value: 'value',
            text: 'text',
            hasChildren: 'hasChild',
            parentValue: 'pId'
        },
        // logic: testLogic,
        itemsSource: 'configStore.getDatasetColumnOptions(this.component.valuesMap.sampleDatasetId)',
        helpText: 'Testing the message'
    },
    itemsSourceOptionsDropdown: {
        label: 'Items Source Options',
        type: 'dropdowntree',
        id: 'itemsSourceOptionsDropdown',
        onchange: 'changeItemsSource',
        itemsSource: 'configStore.itemsSourceOptions()',
        fields: {
            value: 'value',
            text: 'text',
            parentValue: 'pId',
            hasChildren: 'hasChild'
        },
    },
    someMultiSelect: {
        label: 'Multiselect Control',
        id: 'someMultiSelect',
        name: 'testCalcFields',
        type: 'multiselect',
        items: [
            {
                text: 'field 1',
                value: 'field 1'
            },
            {
                text: 'field 2',
                value: 'field 2'
            },
            {
                text: 'field 3',
                value: 'field 3'
            },
            {
                text: 'field 4',
                value: 'field 4'
            },
            {
                text: 'field 5',
                value: 'field 5'
            },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        default: [],
    },
    radioListWithCssClass: {
        label: 'Radio List with cssClass',
        id: 'radioListWithCssClass',
        name: 'radioListWithCssClass',
        type: 'radio_button_list',
        items: [
            {
                text: 'field 1',
                value: 'field 1'
            },
            {
                text: 'field 2',
                value: 'field 2'
            },
            {
                text: 'field 3',
                value: 'field 3'
            },
            {
                text: 'field 4',
                value: 'field 4'
            },
            {
                text: 'field 5',
                value: 'field 5'
            },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        cssClass: 'custom-css-class',
        default: [],
    },
    checkboxListWithCssClass: {
        label: 'Checkbox List with cssClass',
        id: 'checkboxListWithCssClass',
        name: 'checkboxListWithCssClass',
        type: 'checkbox_list',
        items: [
            {
                text: 'field 1',
                value: 'field 1'
            },
            {
                text: 'field 2',
                value: 'field 2'
            },
            {
                text: 'field 3',
                value: 'field 3'
            },
            {
                text: 'field 4',
                value: 'field 4'
            },
            {
                text: 'field 5',
                value: 'field 5'
            },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        cssClass: 'custom-css-class',
        default: [],
    },
    allFieldsInput: {
        label: 'All Fields Input',
        type: 'dropdowntree',
        id: 'allFieldsInput',
        showCheckBox: true,
        allowMultiSelection: true,
        itemsSource: 'configStore.allFieldOptions()',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId'
        },
        helpText: 'Choose the field you want to update.',
        default: ''
    },
    allAutomationsInput: {
        label: 'All Automations Input',
        id: 'allAutomationsInput',
        type: 'dropdowntree',
        itemsSource: 'configStore.allAutomationOptions()',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId'
        },
        helpText: 'Select the automation you want to run.',
        default: ''
    },
    allBcCheckOptionsInput: {
        label: 'Background Checks Input',
        id: 'allBcCheckOptionsInput',
        type: 'dropdown',
        itemsSource: 'configStore.bcPackageOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Select the background check you want to run.',
        default: ''
    },
    allSurveys: {
        label: 'All Surveys Input',
        id: 'allSurveys',
        type: 'dropdowntree',
        itemsSource: 'configStore.allSurveyOptions()',
        fields: {
            text: 'text',
            value: 'value',
            hasChildren: 'hasChild',
            parentValue: 'pId'
        },
        helpText: 'Select a survey.',
        default: ''
    },
    allVols: {
        label: 'All Vols Input',
        id: 'allVols',
        type: 'dropdown',
        itemsSource: 'configStore.allVolOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Select your volunteer.',
        default: ''
    },
    allOpps: {
        label: 'All Opps Input',
        id: 'allOpps',
        type: 'dropdown',
        itemsSource: 'configStore.allOppOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Select your opportunity.',
        default: ''
    },
    allOrgs: {
        label: 'All Orgs Input',
        id: 'allOrgs',
        type: 'dropdown',
        itemsSource: 'configStore.allOrgOptions()',
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Select your organization.',
        default: ''
    },
    dropdownWithAdditionalOptions: {
        label: 'Dropdown Using Item Source & Additional Options',
        type: 'dropdown',
        id: 'dropdownWithAdditionalOptions',
        itemsSource: 'configStore.allOrgOptions()',
        additionalOptions: [
            {
                text: 'aa An additional Option',
                value: 'aaa'
            }
        ],
        itemsSort: {
            sortBy: ['text']
        },
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Additional Options'
    },
    ascendingSortedDropdown: {
        label: 'Test Ascending Sort',
        type: 'dropdown',
        id: 'ascendingSortedDropdown',
        itemsSort: {
            sortBy: 'value'
        },
        items: [
            'Value 1',
            'Value 3',
            'Value 2',
            '4 Value'
        ],
        helpText: 'Testing the message'
    },
    descendingSortedDropdown: {
        label: 'Test Descending Sort',
        type: 'dropdown',
        id: 'descendingSortedDropdown',
        itemsSort: {
            sortBy: 'value',
            direction: 'desc'
        },
        items: [
            'Value 1',
            'Value 3',
            'Value 2',
            '4 Value'
        ],
        helpText: 'Testing the message',
        message: 'Default order: Value 1, Value 3, Value 2, 4 Value'
    },
    numberSortedDropdown: {
        label: 'Test Number Sort',
        type: 'dropdown',
        id: 'numberSortedDropdown',
        itemsSort: {
            sortBy: 'value'
        },
        items: [
            200, // NOSONAR
            2, // NOSONAR
            4, // NOSONAR
            1
        ],
        helpText: 'Testing the message',
        message: 'Default order: 200, 2, 4, 1, output order: 1, 2, 4, 200'
    },
    sortedWithAbsoluteOption: {
        label: 'Test Sort With Absolute Option',
        type: 'dropdown',
        id: 'sortedWithAbsoluteOption',
        itemsSort: {
            sortBy: ['value']
        },
        items: [
            {
                text: 'First Option',
                value: 'a'
            },
            {
                text: 'Second',
                value: 'b'
            },
            {
                text: 'Third',
                value: 'c'
            },
            {
                text: 'Fourth',
                value: 'd'
            },
            {
                text: 'Absolute First',
                value: 'e',
                absoluteOptionPosition: 0
            },
            {
                text: 'Absolute Above Third',
                value: 'f',
                absoluteOptionPosition: 3
            },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Testing the message',
        message: 'sorting options with absolutePosition'
    },
    objectSortedDropdown: {
        label: 'Test Object Sort',
        type: 'dropdown',
        id: 'objectSortedDropdown',
        itemsSort: {
            sortBy: ['exampleProp', 'text']
        },
        items: [
            {
                text: 'opt b',
                value: 'b',
                exampleProp: "c"
            },
            {
                text: 'opt a',
                value: 'a',
                exampleProp: "b"
            },
            {
                text: 'opt c',
                value: 'c',
                exampleProp: "c"
            },
            {
                text: 'opt c',
                value: 'd',
                exampleProp: "a"
            },
        ],
        fields: {
            text: 'text',
            value: 'value'
        },
        helpText: 'Testing the message',
        message: 'sortBy: ["otherProp", "text"], output order: opt c, opt a, opt b, opt c'
    },
    objectArray: {
        label: 'Test Array of Objects',
        type: 'ARRAY',
        id: 'objectArray',
        allowDragAndDrop: true,
        itemConfig: {
            primaryKey: 'textProp',
            itemType: 'object',
            permissions: {
                allowAddItem: true,
                canMove: true,
                canDelete: true
            },
            keys: {
                textProp: {
                    type: 'string',
                    id: 'textProp',
                    label: 'Sample Text in Object',
                    helpText: 'Test for text in object'
                },
                numberProp: {
                    type: 'NUMBER',
                    id: 'numberProp',
                    label: 'Sample Number in Object',
                    helpText: 'Test for number in object'
                },
                colorProp: {
                    type: 'COLOR',
                    id: 'colorProp',
                    label: 'Sample Number in Object',
                    helpText: 'Test for number in object'
                },
                dropdownTreeInArray: {
                    label: 'Dropdown Tree In Array',
                    type: 'dropdowntree',
                    id: 'dropdownTreeInArray',
                    fields: {
                        value: 'value',
                        text: 'text',
                        child: 'child'
                    },
                    default: [],
                    items: [
                        {
                            value: '01',
                            text: 'Music',
                            selectable: false,
                            htmlAttributes: { 'prevent-select': 'true' },
                            child: [
                                {
                                    value: '01-01',
                                    text: 'Gouttes.mp3'
                                }
                            ]
                        },
                        {
                            value: '02',
                            text: 'Videos',
                            selectable: false,
                            htmlAttributes: { 'prevent-select': 'true' },
                            expanded: true,
                            child: [
                                {
                                    value: '02-01',
                                    selectable: false,
                                    htmlAttributes: { 'prevent-select': 'true' },
                                    text: 'Naturals.mp4'
                                },
                                {
                                    value: '02-02',
                                    text: 'Wild.mpeg'
                                }
                            ]
                        }
                    ],
                    helpText: 'Testing the message'
                },
                arrayLogicEditor: {
                    type: 'logicEditor',
                    label: 'Logic Editor in Array',
                    id: 'arrayLogicEditor',
                    helpText: 'Testing logicEditor inside an array of objects.'
                },
                innerItemArray: {
                    label: 'Inner Item Array',
                    id: 'innerItemArray',
                    type: 'ARRAY',
                    default: [],
                    itemConfig: {
                        itemType: 'object',
                        primaryKey: 'dropdown2TreeInArray',
                        permissions: {
                            canMove: false,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            dropdown2TreeInArray: {
                                label: 'Dropdown Tree In Array in Array',
                                type: 'dropdowntree',
                                id: 'dropdown2TreeInArray',
                                fields: {
                                    value: 'value',
                                    text: 'text',
                                    child: 'child'
                                },
                                items: [
                                    {
                                        value: '03',
                                        text: 'Books',
                                        selectable: false,
                                        htmlAttributes: { 'prevent-select': 'true' },
                                        child: [
                                            {
                                                value: '03-01',
                                                text: 'The Great Gatsby'
                                            }
                                        ]
                                    },
                                    {
                                        value: '04',
                                        text: 'Magazines',
                                        expanded: true,
                                        child: [
                                            {
                                                value: '04-01',
                                                selectable: false,
                                                htmlAttributes: { 'prevent-select': 'true' },
                                                text: 'Popular Science'
                                            },
                                            {
                                                value: '04-02',
                                                text: 'Wild.mpeg'
                                            }
                                        ]
                                    }
                                ],
                                helpText: 'Testing the message',
                                default: []
                            },
                            textPropInArray: {
                                type: 'string',
                                id: 'textPropInArray',
                                label: 'Sample Text in Object in Array',
                                helpText: 'Test for text in object'
                            },
                            numberPropInArray: {
                                type: 'NUMBER',
                                id: 'numberPropInArray',
                                label: 'Sample Number in Object in Array',
                                helpText: 'Test for number in object'
                            },
                            colorPropInArray: {
                                type: 'COLOR',
                                id: 'colorPropInArray',
                                label: 'Sample Number in Object in Array',
                                helpText: 'Test for number in object'
                            },
                            nestedArrayLogicEditor: {
                                type: 'logicEditor',
                                label: 'Logic Editor in Nested Array',
                                id: 'nestedArrayLogicEditor',
                                helpText: 'Testing logicEditor inside an array, inside of another array.'
                            },
                        },
                        newItem: {
                            dropdown2TreeInArray: [],
                        }
                    }
                }
            },
            newItem: {
                textProp: 'a new text prop',
                numberProp: 1,
                colorProp: '#000000ff',
            }
        },
        helpText: 'Tests to show how array of objects function'
    },
    objectArrayAllExpanded: {
        label: 'Object Array All Expanded',
        type: 'array',
        id: 'objectArrayAllExpanded',
        expandAll: true,
        hideActions: true,
        hideHeaders: true,
        itemConfig: {
            primaryKey: 'textProp',
            itemType: 'object',
            permissions: {
                allowAddItem: false,
                canMove: false,
                canDelete: false
            },
            keys: {
                arrayProp1: {
                    type: 'string',
                    id: 'arrayProp1',
                    label: 'First Prop'
                },
                arrayProp2: {
                    type: 'string',
                    id: 'arrayProp1',
                    label: 'First Prop'
                },
            },
            newItem: {
                arrayProp1: 'prop1',
                arrayProp2: 'prop2',
            }
        },
        helpText: 'Tests to show how array of objects function'
    },
    objectArrayPrimaryLabel: {
        label: 'Test Array of Objects using primaryLabel Setting',
        type: 'ARRAY',
        id: 'objectArrayPrimaryLabel',
        itemConfig: {
            primaryLabel: 'Primary Label',
            itemType: 'object',
            permissions: {
                allowAddItem: true,
            },
            keys: {
                textProp: {
                    type: 'string',
                    id: 'textProp',
                    label: 'Sample Text in Object',
                    helpText: 'Test for text in object'
                },
                numberProp: {
                    type: 'NUMBER',
                    id: 'numberProp',
                    label: 'Sample Number in Object',
                    helpText: 'Test for number in object'
                }
            },
            newItem: {
                textProp: 'a new text prop',
                numberProp: 1
            }
        },
        helpText: 'Tests to show how primaryLabel works'
    },
    arrayArray: {
        label: 'Test Array of Arrays',
        type: 'ARRAY',
        id: 'arrayArray',
        itemConfig: {
            id: 'arrayItems',
            type: 'ARRAY',
            primaryLabel: 'New Array',
            label: 'Array Items (allowAddItem On)',
            itemType: 'array',
            helpText: 'List of components or HTML in the expanded area of menu item.',
            permissions: {
                allowAddItem: true,
            },
            itemConfig: {
                primaryKey: 'arrayProp1',
                itemType: 'object',
                type: 'ARRAY',
                permissions: {
                    allowAddItem: true,
                },
                keys: {
                    arrayProp1: {
                        type: 'string',
                        id: 'arrayProp1',
                        label: 'A Text Test',
                        helpText: 'Testing the message',
                    },
                    arrayProp2: {
                        type: 'NUMBER',
                        id: 'arrayProp2',
                        label: 'A Number Test',
                        helpText: 'Testing the message',
                    }
                },
                newItem: {
                    arrayProp1: 'testComponent',
                    arrayProp2: 100000
                }
            },
            newItem: [{
                arrayProp1: 'testComponent',
                arrayProp2: 100000
            }]
        },
        default: [],
        helpText: 'Tests to show how array of arrays function'
    },
    arrayArray2: {
        label: 'Test allowAddItems OFF',
        type: 'ARRAY',
        id: 'arrayArray2',
        itemConfig: {
            id: 'arrayItems2',
            type: 'ARRAY',
            primaryKey: 'Array Item',
            label: 'Array Items',
            itemType: 'array',
            helpText: 'List of components or HTML in the expanded area of menu item.',
            permissions: {
                allowAddItem: false,
            },
            itemConfig: {
                primaryKey: 'arrayProp1',
                itemType: 'object',
                type: 'ARRAY',
                keys: {
                    arrayProp1: {
                        type: 'string',
                        id: 'arrayProp1',
                        label: 'A Text Test',
                        helpText: 'Testing the message',
                    },
                    arrayProp2: {
                        type: 'NUMBER',
                        id: 'arrayProp2',
                        label: 'A Number Test',
                        helpText: 'Testing the message',
                    }
                },
                newItem: {
                    arrayProp1: 'testComponent',
                    arrayProp2: 100000
                }
            },
            newItem: [{
                arrayProp1: 'testComponent',
                arrayProp2: 100000
            }]
        },
        default: [],
        helpText: 'Tests to show how array of arrays function'
    },
    someObject: {
        type: 'OBJECT',
        label: 'An Object of Settings',
        id: 'someObject',
        default: {
            objectDropdown: null,
            objectString: null,
            objectNumber: null
        },
        properties: {
            objectDropdown: {
                type: 'dropdown',
                label: 'Test Object Dropdown',
                helpText: 'The Object Dropdown helpText.',
                default: 'Something',
                items: [
                    'Something',
                    'Something2'
                ],
                id: 'objectDropdown'
            },
            objectString: {
                type: 'string',
                label: 'Test Object String',
                helpText: 'The Object String helpText.',
                default: '',
                id: 'objectString'
            },
            objectNumber: {
                type: 'NUMBER',
                label: 'Test Object Number',
                helpText: 'The Object Number helpText.',
                default: null,
                id: 'objectNumber'
            }
        }
        // valueType: 'Category',
        // labelPlacement: 'OnTicks'
    },
    someLogicField: {
        name: 'someLogicField',
        label: 'Some Logic Editor',
        type: 'logicEditor',
        componentId: 'featureTestBench',
        helpText: 'Setup some logic.'
    },
    configuredActions: {
        label: 'Configured Actions',
        type: 'array',
        itemConfig: {
            primaryKey: 'actionLabel',
            itemType: 'object',
            keys: {
                actionLabel: {
                    type: 'string',
                    label: 'Action Button Label',
                    default: 'Some Action',
                    helpText: 'Set the text for the action button.'
                },
                configuredAction: {
                    type: 'actionfield',
                    id: 'configuredAction',
                    itemsSource: 'actionStore.getActionOptions()',
                    label: 'Configured Action',
                    helpText: 'Testing the message',
                    allowDragAndDrop: true,
                    fields: {
                        text: 'text',
                        value: 'value'
                    },
                }
            },
            newItem: {
                actionLabel: 'Some Action',
                configuredAction: []
            },
            permissions: {
                canMove: false,
                canDelete: true,
                allowAddItem: true
            }
        },
    },
    testCalc: {
        label: 'Calculated Value',
        id: 'testCalc',
        type: 'calccontrol',
        itemsSource: 'this.$parent.calcFields',
        default: [],
        helpText: 'Testing the message',
    },
    someDatasourceField: {
        label: 'Test Datasource',
        id: 'someDatasourceField',
        type: 'datasource',
        itemsSource: 'configStore.getDatasourceList()',
        fields: {
            text: 'text',
            value: 'value',
            parentValue: 'pId',
            hasChildren: 'hasChild'
        },
        allowFiltering: true,
        default: [],
        helpText: 'Testing the message',
    },
    column1Field: {
        label: 'Column 1 Field',
        id: 'column1Field',
        type: 'string',
        helpText: 'Test for column 1 field.',
        default: '',
        column: 1
    },
    column2Field: {
        label: 'Column 2 Field',
        id: 'column2Field',
        type: 'number',
        helpText: 'Test for column 2 field.',
        default: 4,
        column: 2
    },
    column3Field: {
        label: 'Column 3 Field',
        id: 'column3Field',
        type: 'dropdown',
        helpText: 'Test for column 3 field.',
        items: [
            'test item 1',
            'test item 2',
            'test item 3'
        ],
        column: 3
    }
};

const featureTestBenchConfig = {
    cId: "featureTestBench",
    cType: "featureTestBench",
    cTitle: "Feature Test Bench",
    cTemplate: `<div class="settings-editor-sample-component" v-if="config && !configReload">
        <ejs-tab id="sample_settings_editor_tabs">
            <div class="e-tab-header">
                <div data-test-id="'_tab_header_settings_editor'">Settings Editor</div>
                <div data-test-id="'_tab_header_test_settings'">Settings Models</div>
                <div data-test-id="'_tab_header_test_map'">Test Map</div>
                <div data-test-id="'_tab_header_test_value_types'">Test Value Types</div>
                <div data-test-id="'_tab_header_logic_parser_tests'">Logic Tests</div>
                <div data-test-id="'_tab_header_properties_test'">Properties Test</div>
                <div data-test-id="'_tab_header_actions_test'">Actions Test</div>
                <div data-test-id="'_tab_header_dialog_test'">Subscription Tests</div>
                <div data-test-id="'_tab_header_dialog_test'">Dialogs Test</div>
                <div data-test-id="'_tab_header_calc_test'">Calculations Test</div>
                <div data-test-id="'_tab_header_list_control'">List Control Test</div>
                <div data-test-id="'_tab_header_object_control'">Object Control Test</div>
                <div data-test-id="'_tab_header_pdf_viewer_test'">PDF Test</div>
                <div data-test-id="'_tab_header_error_repeater_test'">Error Repeater Test</div>
                <div data-test-id="'_tab_header_dt_test'">Current Date/Time Test</div>
                <div data-test-id="'_tab_header_udf_test'">udfDetailsPanel Test</div>
                <div data-test-id="'_tab_header_dd_test'">DataDictionary Test</div>
                <div data-test-id="'_tab_header_pb_test'">ProgressBar Test</div>
                <div data-test-id="'_tab_header_up_test'">Attachments Test</div>
                <div data-test-id="'_tab_header_bs_test'">Button Styles Test</div>
                <div data-test-id="'_tab_header_w_test'">Wizard Test</div>
            </div>
            <div class="e-content">
                <div>
                    <h1>Settings Editor</h1>
                    <settings-editor :c-id="'settingsEditor2__' + cId" :prop-component-id="cId" :editor-enabled="previewEditorEnabled" :prop-test-mode="previewTestMode"></settings-editor>
                </div>
                <div>
                    <h1>Settings Models</h1>
                    <control name="settingsModel" :value="selectedSettingsModel" @update:value="selectedSettingsModel = $event"></control>
                    <div v-if="selectedSettingsModel === 'displayedConfig'">
                        <json-viewer :value="displayedConfig"></json-viewer>
                    </div>
                    <div v-if="selectedSettingsModel === 'configStore'">
                        <json-viewer :value="configStore"></json-viewer>
                    </div>
                    <div v-if="selectedSettingsModel === 'userStore'">
                        <json-viewer :value="userStore"></json-viewer>
                    </div>
                    <div v-if="selectedSettingsModel === 'componentStore'">
                        <json-viewer :value="componentStore.state"></json-viewer>
                    </div>
                    <div v-if="selectedSettingsModel === 'actionStore'">
                        <json-viewer :value="actionStore"></json-viewer>
                    </div>
                    <div v-if="selectedSettingsModel === 'featureTestBenchControls'">
                        <json-viewer :value="controls"></json-viewer>
                    </div>
                </div>
                <div>
                    <h1>Test Map</h1>
                    <pre>{{editorSettings}}</pre>
                </div>
                <div>
                    <h1>Test Value Types</h1>
                    <ul class="test-value-types">
                        <li v-for="(field) in testTypes">
                            <span><strong>{{editorSettings[field.key].label}}</strong></span><br/>
                            <span><strong>Value:</strong> {{field.val}}</span><br/>
                            <span :data-test-id="'sample_settings_editor_typeTest_' + field.key + '_type'"><strong>Type:</strong> {{typeof field.val}}</span>
                        </li>
                    </ul>
                </div>
                <div>
                    <h1>Logic Tests</h1>
                    <div>
                        <div v-if="!volAffRecords || !oppRefPlRecords" style="font-size: 18px; padding: 10px; background-color: #eee; margin-bottom: 10px;">
                            Set context vol to test Affiliation or refer/place logic.  If you've already done this, check the network tab.
                        </div>
                    </div>
                    <div>
                        <control name="setSomeLogic" :value="testLogicControlValue" @update:value="testLogicControlValue = $event"></control>
                        <div class="logic-test">
                            <div>
                                <control name="logicTestInstructions"></control>
                                <control name="logicBooleanControl" :value="logicBoolean" @update:value="logicBoolean = $event"></control>
                                <control name="logicNumberControl" :value="logicNumber" @update:value="logicNumber = $event"></control>
                                <ejs-button v-on:click="logicNumber = null">
                                    Set Logic Number to NULL
                                </ejs-button>
                                <control name="logicStringControl" :value="logicString" @update:value="logicString = $event"></control>
                                <control name="logicDateControl" :value="logicDate" @update:value="logicDate = $event"></control>
                                <ejs-button v-on:click="logicDate = null">
                                    Set Logic Date to NULL
                                </ejs-button>
                                <control name="logicTimeControl" :value="logicTime" @update:value="logicTime = $event"></control>
                                <control name="logicDropdownControl" :value="logicDropdown" @update:value="logicDropdown = $event"></control>
                                <control name="logicDropdownTreeControl" :value="logicDropdowntree" @update:value="logicDropdowntree = $event"></control>
                                <control name="logicArrayControl" :value="logicArray" @update:value="logicArray = $event"></control>
                                <control name="logicObjectArrayControl" :value="logicObjectArray" @update:value="logicObjectArray = $event"></control>
                                <hr/>
                                <control name="relativeRecordInstructions"></control>
                                <control name="relativeVolIdControl" :value="localVolId" @update:value="localVolId = $event" :actionParams="'volId'"></control>
                                <control name="relativeOppIdControl" :value="localOppId" @update:value="localOppId = $event" :actionParams="'oppId'"></control>
                                <control name="relativeOrgIdControl" :value="localOrgId" @update:value="localOrgId = $event" :actionParams="'orgId'"></control>
                                <control name="relativeLbeIdControl" :value="localLbeId" @update:value="localLbeId = $event" :actionParams="'lbeId'"></control>
                            </div>
                            <div class="logic-test-result">
                                <p>logicResult1: <span data-test-id="logic_test_result1">{{logicResult1}}</span></p>
                                <p>logicResult2: <span data-test-id="logic_test_result2">{{logicResult2}}</span></p>
                                <p>logicResult3: <span data-test-id="logic_test_result3">{{logicResult3}}</span></p>
                                <p>logicResult4: <span data-test-id="logic_test_result4">{{logicResult4}}</span></p>
                                <p>logicBoolean: {{logicBoolean}}</p>
                                <p>logicNumber: {{logicNumber}} {{logicNumber === null ? "null" : ""}}</p>
                                <p>logicString: {{logicString}}</p>
                                <p>logicDate: {{logicDate}} {{logicDate === null ? "null" : ""}}</p>
                                <p>logicTime: {{logicTime}} {{logicTime === null ? "null" : ""}}</p>
                                <p>logicArray: {{logicArray}}</p>
                                <p>Relative Records:<br/>
                                    volId: {{volId}}<br/>
                                    oppId: {{oppId}}<br/>
                                    orgId: {{orgId}}<br/>
                                    lbeId: {{lbeId}}<br/>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <control name="setSomeChangeType"></control>
                        <control name="someChangeType"></control>
                    </div>
                    <control name="showFieldsLogic"></control>
                    <control name="makeFieldsRequiredLogic"></control>
                    <control name="disableFieldsLogic"></control>
                    <control name="changeLabelsLogic"></control>
                    <control name="someStringLogic"></control>
                    <control name="someTextLogic"></control>
                    <control name="someDropdownLogic"></control>
                    <control name="someMultiselectLogic"></control>
                    <control name="someDropdownTreeLogic"></control>
                    <control name="someObjectLogic"></control>
                </div>
                <div>
                    <h1>Properties Test</h1>
                    testMode: {{testMode}}<br/>
                    testProperty: {{testProperty}}<br/>
                </div>
                <div>
                    <h1 style="margin: 20px 0;">Actions Test</h1>
                    <h2 style="margin: 20px 0;">Configured Actions</h2>
                    <div v-for="(action, index) in displayedConfig.configuredActions">
                        <ejs-button v-on:click="runTestAction(false, index)" :data-test-id="dataTestId">
                            {{action.actionLabel}}
                        </ejs-button>
                    </div>
                    <h3 style="margin: 20px 0;">Action Param Wizard Test</h3>
                    <control name="actionType" :value="typeOfAction"  @update:value="typeOfAction = $event"></control>
                    <div>
                        <control name="predefinedParams" :value="predefinedParams"  @update:value="predefinedParams = $event"></control>
                    </div>
                    <div v-if="typeOfAction === 'openUrl'" class="alert alert-primary">
                        The open URL action parameters cannot be defined through the missing params wizard.  They must always be defined beforehand.  If a required parameter is missing, a message is displayed instead.
                    </div>
                    <ejs-button v-on:click="runTestAction(typeOfAction)" :data-test-id="dataTestId">
                        Test {{typeOfActionText}} Action
                    </ejs-button>
                </div>
                <div>
                    <h1 style="margin: 20px 0;">Async Subscription Test</h1>
                    <div class="dialog-test-buttons">
                        <control name="testAsyncSub"></control>
                        Subscription time to ready: {{timeToResponse}}
                    </div>
                </div>
                <div>
                    <h1 style="margin: 20px 0;">Dialogs Test</h1>
                    <div class="dialog-test-buttons">
                        <control name="dialog1"></control>
                        <control name="dialog2"></control>
                        <control name="dialog3"></control>
                        <control name="dialog4"></control>
                    </div>
                    <h2>Dialogs with Response <small>(systematic close closes after 2 second delay)</small></h2>
                    <div class="dialog-test-buttons">
                        <control name="dialog5"></control>
                        <control name="successDialog5"></control>
                        <control name="cancelDialog5"></control>
                    </div>
                    <div>
                        <div v-if="dialogResponse2" style="margin-top: 20px; padding: 10px; background: #eee;"><strong>{{dialogResponse2}}</strong></div>
                        <pre>{{dialogResponse}}</pre>
                    </div>
                    <h1 style="margin: 20px 0;">Tooltip Test</h1>
                    <div style="margin: 50px 0;">
                        <sam-tooltip
                            ref="tooltip"
                            opensOn="Click"
                            content="<strong>Note:</strong> Test Tooltip"
                            label="Click to test tooltip"
                            label-classes=""
                            id="some_test_tooltip"
                        ></sam-tooltip>
                    </div>
                </div>
                <div>
                    <h1>Calculations Test</h1>
                    Field Values:
                    <pre>{{calcValues}}</pre>
                    <control name="testCalcFields"></control>
                    <control name="testCalcControl"></control>
                    <div class="calc-result">{{calcResult}}</div>
                </div>
                <div>
                    <h1>List Control Test</h1>
                    <control name="toggleListControl"></control>
                    <control name="testListControl" v-if="showListControl"></control>
                </div>
                <div>
                    <h1>Object Control Test</h1>
                    <control name="testObjectControl" :value="testObjectControlValue" @update:value="testObjectControlValue = $event;"></control>
                    <pre>{{testObjectControlValue}}</pre>
                    <control name="testObjectControl2" :value="testObjectControlValue2" @update:value="testObjectControlValue2 = $event;"></control>
                    <pre>{{testObjectControlValue2}}</pre>
                </div>
                <div>
                    <h1>(Disabled for now)</h1>
                    <!--<h1>1)Mockup: Add form elements, click save and reload following control</h1>
                    <control name="pdfEditor"></control>
                    <h1>2) Pre-filled pdf forms: Check pre-filled form, fill other fields, click save and reload following control</h1>
                    <control name="pdfViewer" :vol-id="271483"></control>
                    <h1>3) Final version: Check the result</h1>
                    <control name="pdfViewer1"></control>-->
                </div>
                <div>
                    <h1>Error Repeater Test</h1>
                    <control name="testErrorBtn"></control>
                </div>
                <div>
                    <h1>Current Date/Time Test</h1>
                    <button @click="showActiveDateTime = !showActiveDateTime"></button>
                    <div class="date-time-display" v-if="showActiveDateTime">
                        <div>
                            <span>Current Date: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentdate">{{configStore.currentDate}}</span>
                        </div>
                        <div>
                            <span>Current Time: </span>
                            <span data-test-id="sample_settings_editor_datetime_currenttime">{{configStore.currentTime}}</span>
                        </div>
                        <div>
                            <span>Current Date and Time: </span>
                            <span data-test-id="sample_settings_editor_datetime_currenttime">{{configStore.currentDateTime}}</span>
                        </div>
                        <div>
                            <span>Current Year: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentyear">{{configStore.currentYear}}</span>
                        </div>
                        <div>
                            <span>Current Month Number: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentmonth">{{configStore.currentMonth}}</span>
                        </div>
                        <div>
                            <span>Current Month Name: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentmonthname">{{configStore.currentMonthName}}</span>
                        </div>
                        <div>
                            <span>Current Weekday Number: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentweekday">{{configStore.currentWeekDay}}</span>
                        </div>
                        <div>
                            <span>Current Weekday Name: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentweekdayname">{{configStore.currentWeekDayName}}</span>
                        </div>
                        <div>
                            <span>Current Day of Month: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentmonthday">{{configStore.currentMonthDay}}</span>
                        </div>
                        <div>
                            <span>Current Day of Year: </span>
                            <span data-test-id="sample_settings_editor_datetime_currentyearday">{{configStore.currentYearDay}}</span>
                        </div>
                    </div>
                </div>
                <div>
                    <h1>udfDetailsPanel Test</h1>
                    <br/><br/>
                    <udfDetailsPanel cId="udfDetailsPanel__1" :prop-code-id="'UDF_CHECK_(do_not_remove)/Attachment'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__2" :prop-code-id="'UDF_CHECK_(do_not_remove)/Devider'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__3" :prop-code-id="'UDF_CHECK_(do_not_remove)/DropdownList'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__4" :prop-code-id="'UDF_CHECK_(do_not_remove)/DropdownListWithValues'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__5" :prop-code-id="'UDF_CHECK_(do_not_remove)/Identifier'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__6" :prop-code-id="'UDF_CHECK_(do_not_remove)/Identifier'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__7" :prop-code-id="'UDF_CHECK_(do_not_remove)/LongText'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__8" :prop-code-id="'UDF_CHECK_(do_not_remove)/Number'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__9" :prop-code-id="'UDF_CHECK_(do_not_remove)/Percent'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__10" :prop-code-id="'UDF_CHECK_(do_not_remove)/Rating'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__11" :prop-code-id="'UDF_CHECK_(do_not_remove)/ShortText'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__12" :prop-code-id="'UDF_CHECK_(do_not_remove)/Time'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__13" :prop-code-id="'UDF_CHECK_(do_not_remove)/url'"></udfDetailsPanel>
                    <udfDetailsPanel cId="udfDetailsPanel__14" :prop-code-id="'UDF_CHECK_(do_not_remove)/Verification'"></udfDetailsPanel>
                </div>
                <div>
                    <h1>DataDictionary Test</h1>
                    <br/><br/>
                    COMMON.BUTTON_APPLY -- {{ddStore.COMMON.BUTTON_APPLY}}<br/>
                    SIGNIN2_OPTIONS.SIGN_IN_STATION_ADMIN -- {{ddStore.SIGNIN2_OPTIONS.SIGN_IN_STATION_ADMIN}}<br/><br/>
                    <br/>
                    Label test
                    <control name="ddTest"></control>
                    <control name="ddTest1"></control>
                    <br/>
                    <br/>
                    Placeholder test
                    <control name="ddTest2"></control>
                    <br/>
                    <br/>
                    Items test
                    <control name="ddTest3"></control>
                </div>
                <div>
                    <h1>Progress Bar Test</h1>
                    <br/><br/>
                    <control name="progress1" :value="progress1Value" @update:value="progress1Value = $event"></control>
                    <div>{{progress1Value}}</div>
                    <control name="progress2" :value="progress2Value" @update:value="progress2Value = $event"></control>
                    <div>{{progress2Value}}</div>
                    <control name="startProgress"></control>
                </div>
                <div>
                    <h1>Attachments Test</h1>
                    <h2>Controls Examples</h2>
                    <div class="upload-img-container">
                        <div v-for="(file) in uploadValue0" class="upload-img-item">
                            <img :src="file.base64" :alt="file.name" :title="file.name" width="100%"/>
                        </div>
                    </div>
                    <control name="UPLOAD1" :value="uploadValue1" @update:value="uploadValue1 = $event"></control>
                    <div class="upload-img-container">
                        <div v-for="(file) in uploadValue1" class="upload-img-item">
                            <img :src="file.base64" :alt="file.name" :title="file.name" width="100%"/>
                        </div>
                    </div>
                    <control name="UPLOAD2" :value="uploadValue2" @update:value="uploadValue2 = $event"></control>
                    <div class="upload-img-container">
                        <div v-for="(file) in uploadValue2" class="upload-img-item">
                            <img :src="file.base64" :alt="file.name" :title="file.name" width="100%"/>
                        </div>
                    </div>
                    <ejs-button v-on:click="runTestUpload()">
                        Run Upload Test
                    </ejs-button>
                    <hr/>
                    <h2>Single Upload Test</h2>
                    <p style="padding: 10px; background: #eee; border:1px solid #aaa; border-radius: 4px; font-size: 16px;">
                        <strong>Record Used</strong>
                        ID: 106820<br/>
                        Name: David Bowie
                    </p>
                    <control name="UPLOAD0" :value="uploadValue0" @update:value="uploadValue0 = $event" :recordId="'106820'" :psp="'vol'"></control>
                    <hr/>
                    <h2>URL Download/Link Test</h2>
                    <div data-test-id="attachment_url_test" style="margin-top: 20px; padding: 10px; background: #eee; border: 1px solid #ddd; border-radius: 4px; display: flex; flex-direction: row;">
                        <div style="margin-right: 20px;">
                            <img data-test-id="attachment_url_test_image" src="https://dev.samaritan.com/custom/sds.php?sDs_ga=94E4B92754BED363AC1D553AEE2197E4F2FFA537BC64E8F92C85B9919D293B34" alt="Test Image" />
                        </div>
                        <div style="word-break: break-all;">
                            Image source:<br/>
                            <span data-test-id="attachment_url_test_src">https://dev.samaritan.com/custom/sds.php?sDs_ga=94E4B92754BED363AC1D553AEE2197E4F2FFA537BC64E8F92C85B9919D293B34</span>
                        </div>
                    </div>
                </div>
                <div>
                    <h1>Button Styles Test</h1>
                    <br/><br/>
                    <control name="BUTTON2" style="float: left"></control>
                    <control name="BUTTON3" style="float: left"></control>
                    <control name="BUTTON4" style="float: left"></control>
                    <control name="BUTTON5" style="float: left"></control>
                    <div class="clearfix"></div>
                    <br/><br/>
                    <control name="BUTTON6" style="float: left"></control>
                    <control name="BUTTON7" style="float: left"></control>
                    <control name="BUTTON8" style="float: left"></control>
                    <control name="BUTTON9" style="float: left"></control>
                    <div class="clearfix"></div>
                    <br/><br/>
                    <control name="BUTTON10" style="float: left"></control>
                    <control name="BUTTON11" style="float: left"></control>
                    <control name="BUTTON12" style="float: left"></control>
                    <control name="BUTTON13" style="float: left"></control>
                    <div class="clearfix"></div>
                    <br/><br/>
                    <control name="BUTTON14" style="float: left"></control>
                    <control name="BUTTON15" style="float: left"></control>
                    <control name="BUTTON16" style="float: left"></control>
                    <control name="BUTTON17" style="float: left"></control>
                    <div class="clearfix"></div>
                </div>
                <div>
                    <h1>Wizard Test</h1>
                    <br/><br/>
                    <tab-list c-id="tabList_wizard" :prop-validation-func="validationFunc"></tab-list>
                </div>
            </div>
        </ejs-tab>
    </div>`,
    controls: [
        {
            name: 'BUTTON2',
            type: 'button',
            label: 'cssClass e-success',
            cssClass: 'e-success',
            width: '25%'
        },
        {
            name: 'BUTTON3',
            type: 'button',
            label: 'cssClass e-info',
            cssClass: 'e-info',
            width: '25%'
        },
        {
            name: 'BUTTON4',
            type: 'button',
            label: 'cssClass e-warning',
            cssClass: 'e-warning',
            width: '25%'
        },
        {
            name: 'BUTTON5',
            type: 'button',
            label: 'cssClass e-danger',
            cssClass: 'e-danger',
            width: '25%'
        },
        {
            name: 'BUTTON6',
            type: 'button',
            label: 'cssClass e-link',
            cssClass: 'e-link',
            width: '25%'
        },
        {
            name: 'BUTTON7',
            type: 'button',
            label: 'cssClass e-flat',
            cssClass: 'e-flat',
            width: '25%'
        },
        {
            name: 'BUTTON8',
            type: 'button',
            label: 'cssClass e-outline',
            cssClass: 'e-outline',
            width: '25%'
        },
        {
            name: 'BUTTON9',
            type: 'button',
            label: 'cssClass e-round',
            cssClass: 'e-round',
            width: '25%'
        },
        {
            name: 'BUTTON10',
            type: 'button',
            label: 'cssClass e-link + colors',
            cssClass: 'e-link',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON11',
            type: 'button',
            label: 'cssClass e-flat + colors',
            cssClass: 'e-flat',
            backgroundColor: 'white',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON12',
            type: 'button',
            label: 'cssClass e-outline + colors',
            cssClass: 'e-outline',
            backgroundColor: 'white',
            borderColor: '#ff6d53',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON13',
            type: 'button',
            label: 'cssClass e-round + colors',
            cssClass: 'e-round',
            backgroundColor: 'white',
            borderColor: '#ff6d53',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON10',
            type: 'button',
            label: 'cssClass e-link + colors',
            cssClass: 'e-link',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON11',
            type: 'button',
            label: 'cssClass e-flat + colors',
            cssClass: 'e-flat',
            backgroundColor: 'white',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON12',
            type: 'button',
            label: 'cssClass e-outline + colors',
            cssClass: 'e-outline',
            backgroundColor: 'white',
            borderColor: '#ff6d53',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON13',
            type: 'button',
            label: 'cssClass e-round + colors',
            cssClass: 'e-round',
            backgroundColor: 'white',
            borderColor: '#ff6d53',
            textColor: '#ff6d53',
            width: '25%'
        },
        {
            name: 'BUTTON14',
            type: 'button',
            label: 'cssClass e-link + icon left',
            cssClass: 'e-link',
            iconCss: 'e-icons e-arrow-right',
            iconPosition: 'Left',
            width: '25%'
        },
        {
            name: 'BUTTON15',
            type: 'button',
            label: 'cssClass e-flat + icon right',
            iconCss: 'e-icons e-arrow-right',
            iconPosition: 'Right',
            width: '25%'
        },
        {
            name: 'BUTTON16',
            type: 'button',
            label: 'cssClass e-outline + icon top',
            cssClass: 'e-outline',
            iconCss: 'e-icons e-arrow-right',
            iconPosition: 'Top',
            width: '25%'
        },
        {
            name: 'BUTTON17',
            type: 'button',
            label: 'cssClass e-round + icon bottom',
            cssClass: 'e-round',
            iconCss: 'e-icons e-arrow-right',
            iconPosition: 'Bottom',
            width: '25%'
        },
        {
            name: 'settingsModel',
            width: '300px',
            label: 'Settings Model',
            type: 'dropdown',
            default: 'displayedConfig',
            items: [
                'displayedConfig',
                'configStore',
                'componentStore',
                'actionStore',
                'userStore',
                'featureTestBenchControls'
            ],
            helpText: 'Select a settings model to view the structure as a JSON object',
            message: 'Settings models are represented as JSON objects. Choose a model and browse the object below.'
        },
        {
            name: 'logicTestInstructions',
            type: 'alert',
            // eslint-disable-next-line max-len
            value: '<strong>Instructions:</strong><br/><ol style="padding-left: 15px;"><li>Set the logic editor criteria to match a value for any of the controls: logicBoolean, logicNumber, logicString, logicArray, logicObjectArray</li><li>Set the logic editor to change the value of any of logicResult properties: logicResult1, logicResult2, logicResult3, logicResult4.</li><li>Use the controls below to change the properties for any of: logicBoolean, logicNumber, logicString, logicArray.</li><li>You should see the values for logicResult1 - 4 change when all logic criteria is met.</li></ol>'
        },
        {
            name: 'logicBooleanControl',
            label: 'Logic Switch',
            type: 'boolean',
            default: false,
            helpText: 'Toggle the boolean value after setting up your test logic.'
        },
        {
            name: 'logicNumberControl',
            label: 'Logic Number',
            type: 'number',
            default: 0,
            helpText: 'Set the number value after setting up your test logic.'
        },
        {
            name: 'logicStringControl',
            label: 'Logic String',
            type: 'string',
            default: false,
            helpText: 'Set the string value after setting up your test logic.'
        },
        {
            name: 'logicDateControl',
            label: 'Logic Date',
            type: 'date',
            default: false,
            helpText: 'Set the date value after setting up your test logic.'
        },
        {
            name: 'logicTimeControl',
            label: 'Logic Time',
            type: 'time',
            default: null,
            helpText: 'Set the time value after setting up your test logic.'
        },
        {
            name: 'logicDropdownControl',
            label: 'Logic Dropdown',
            type: 'dropdown',
            items: [
                {
                    text: 'Option 1',
                    value: 'option1'
                },
                {
                    text: 'Option 2',
                    value: 'option2'
                }
            ],
            fields: {
                text: 'text',
                value: 'value'
            },
            default: false,
            helpText: 'Set the dropdown value after setting up your test logic.'
        },
        {
            label: 'Logic Dropdown Tree',
            type: 'dropdowntree',
            name: 'logicDropdownTreeControl',
            items: [
                {
                    text: 'Parent A',
                    value: 'parenta',
                    hasChild: true
                },
                {
                    text: 'Option 1 (value is option1)',
                    value: 'option1',
                    pId: 'parenta'
                },
                {
                    text: 'Option 2 (value is option2)',
                    value: 'option2',
                    pId: 'parenta'
                }
            ],
            fields: {
                text: 'text',
                value: 'value',
                parentValue: 'pId',
                hasChildren: 'hasChild'
            },
            helpText: 'Testing the message'
        },
        {
            name: 'logicDropdownControl',
            label: 'Logic Dropdown',
            type: 'dropdown',
            items: [
                {
                    text: 'Option 1',
                    value: 'option1'
                },
                {
                    text: 'Option 2',
                    value: 'option2'
                }
            ],
            fields: {
                text: 'text',
                value: 'value'
            },
            default: false,
            helpText: 'Set the dropdown value after setting up your test logic.'
        },
        {
            name: 'logicArrayControl',
            label: 'Logic Array',
            type: 'multiselect',
            allowCustomValue: true,
            items: [
                {
                    text: 'item 1',
                    value: 'item 1'
                },
                {
                    text: '555',
                    value: 555
                },
                {
                    text: 'blue',
                    value: 'blue'
                },
            ],
            fields: {
                text: 'text',
                value: 'value'
            },
            default: [],
            helpText: 'Toggle the switch after setting up your test logic.'
        },
        {
            name: 'logicObjectArrayControl',
            label: 'Logic Object Array',
            type: 'array',
            itemConfig: {
                itemType: 'object',
                primaryKey: 'text',
                keys: {
                    text: {
                        type: 'string',
                        id: 'text',
                        label: 'Text',
                        helpText: 'Set the text of the array item.',
                        default: 'test a'
                    },
                    value: {
                        type: 'string',
                        id: 'value',
                        label: 'Value',
                        helpText: 'Set the value of the array item.',
                        default: 'test a value'
                    }
                },
                permissions: {
                    canMove: false,
                    canDelete: true,
                    allowAddItem: true
                },
                newItem: {
                    text: 'Text 2',
                    value: 'Value 2'
                }
            },
            default: [],
            helpText: 'Toggle the switch after setting up your test logic.'
        },
        {
            name: 'relativeRecordInstructions',
            type: 'alert',
            value: 'Set relative records to mimic what the logic editor would display when a record is passed into the layout via a param. eg: &lt;layout c-id="some_layout" :vol-id="[VOLUNTEER_ID]"&gt;&lt;/layout&gt;'
        },
        {
            name: 'relativeVolIdControl',
            label: 'Volunteer Relative ID',
            type: 'number',
            default: 0,
            helpText: 'Set the Volunteer Relative ID on the parent layout store.',
            onchange: 'setRelativeId'
        },
        {
            name: 'relativeOppIdControl',
            label: 'Opportunity Relative ID',
            type: 'number',
            default: 0,
            helpText: 'Set the Opportunity Relative ID on the parent layout store.',
            onchange: 'setRelativeId'
        },
        {
            name: 'relativeOrgIdControl',
            label: 'Organization Relative ID',
            type: 'number',
            default: 0,
            helpText: 'Set the Organization Relative ID on the parent layout store.',
            onchange: 'setRelativeId'
        },
        {
            name: 'relativeLbeIdControl',
            label: 'Logbook Relative ID',
            type: 'number',
            default: 0,
            helpText: 'Set the Logbook Relative ID on the parent layout store.',
            onchange: 'setRelativeId'
        },
        {
            name: 'setSomeLogic',
            label: 'Logic Editor Control',
            type: 'logicEditor',
            componentId: 'featureTestBench',
            default: [{}],
            helpText: 'Setup some logic.'
        },
        {
            name: 'setSomeChangeType',
            type: 'dropdown',
            label: 'Change the Field Type',
            items: [
                'boolean',
                'string',
                'number',
                'date'
            ],
            default: 'string'
        },
        {
            label: 'Test Change Type',
            type: 'string',
            name: 'someChangeType',
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: [
                {
                    criteriaRecord: 'component',
                    record: 'self',
                    prop: 'type',
                    valueType: 'control',
                    valueControl: 'setSomeChangeType',
                    criteria: 'true'
                }
            ]
        },
        {
            name: 'makeFieldsRequiredLogic',
            type: 'boolean',
            label: 'Make Fields Required',
            default: false
        },
        {
            name: 'disableFieldsLogic',
            type: 'boolean',
            label: 'Disable Fields',
            default: false
        },
        {
            name: 'changeLabelsLogic',
            type: 'boolean',
            label: 'Change Labels',
            default: false
        },
        {
            name: 'showFieldsLogic',
            type: 'boolean',
            label: 'Show Fields',
            default: true
        },
        {
            label: 'Logic Result: Test Some String',
            type: 'string',
            name: 'someStringLogic',
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: testLogic
        },
        {
            label: 'Logic Result: Test Some Text',
            type: 'text',
            name: 'someTextLogic',
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: testLogic
        },
        {
            label: 'Logic Result: Test Some Dropdown',
            type: 'dropdown',
            name: 'someDropdownLogic',
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: testLogic
        },
        {
            label: 'Logic Result: Test Some Multiselect Dropdown',
            type: 'multiselect',
            name: 'someMultiselectLogic',
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: testLogic
        },
        {
            label: 'Logic Result: Test Some Dropdown Tree',
            type: 'dropdowntree',
            name: 'someDropdownTreeLogic',
            items: [
                {
                    text: 'Parent A',
                    value: 'parenta',
                    hasChild: true
                },
                {
                    text: 'Option 1 (value is option1)',
                    value: 'option1',
                    pId: 'parenta'
                },
                {
                    text: 'Option 2 (value is option2)',
                    value: 'option2',
                    pId: 'parenta'
                }
            ],
            fields: {
                text: 'text',
                value: 'value',
                parentValue: 'pId',
                hasChildren: 'hasChild'
            },
            helpText: 'Testing the message',
            cssClass: 'e-outline',
            logic: testLogic
        },
        {
            type: 'OBJECT',
            label: 'Some Object Logic Test',
            helpText: 'Testing display logic for object',
            name: 'someObjectLogic',
            default: {},
            properties: {
                someProperty: {
                    type: 'string',
                    label: 'Some Sub Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'someProperty',
                    logic: testLogic
                },
            },
            logic: testLogic
        },
        {
            label: 'Action Type',
            id: 'actionType',
            name: 'actionType',
            type: 'dropdown',
            itemsSource: 'actionStore.getActionOptions()',
            fields: {
                text: 'text',
                value: 'value'
            },
            onchange: 'resetPredefinedParams',
            default: [],
        },
        {
            label: 'Predefined Params',
            id: 'predefinedParams',
            name: 'predefinedParams',
            type: 'checkbox_list',
            items: getActionStoreRequiredParams(),
            itemsFilter: 'this.$parent.component.typeOfAction === this.item.action',
            fields: {
                text: 'text',
                value: 'value'
            },
            default: null
        },
        {
            label: 'Fields to use in calculation test',
            id: 'testCalcFields',
            name: 'testCalcFields',
            type: 'multiselect',
            items: [
                {
                    text: 'field 1',
                    value: 'field 1'
                },
                {
                    text: 'field 2',
                    value: 'field 2'
                },
                {
                    text: 'field 3',
                    value: 'field 3'
                },
                {
                    text: 'field 4',
                    value: 'field 4'
                },
            ],
            fields: {
                text: 'text',
                value: 'value'
            },
            default: [],
        },
        {
            label: 'Toggle List',
            id: 'toggleListControl',
            name: 'toggleListControl',
            type: 'button',
            onclick: 'toggleList'
        },
        {
            label: 'List Control',
            id: 'testListControl',
            name: 'testListControl',
            type: 'list',
            items: [
                {
                    aProp: 'something',
                    bProp: 'something else'
                },
                {
                    aProp: 'something 2',
                    bProp: 'something else 2'
                }
            ],
            itemContentTemplate: `<div style="padding: 20px; border: 1px solid blue; margin: 20px;">
                <div :id="'aProp_' + data.key">aProp: <span :id="'prop-value-aprop_' + data.key">{{data.aProp}}</span></div>
                <div :id="'aProp_' + data.key">bProp: <span :id="'prop-value-bprop_' + data.key">{{data.bProp}}</span></div>
                <control :name="'INNER_CONTROL1__' + data.key"></control>
                <control name="INNER_CONTROL2"></control>
            </div>`,
        },
        {
            label: 'Object Control',
            id: 'testObjectControl',
            name: 'testObjectControl',
            type: 'object',
            // default: {},
            properties: {
                stringProp: {
                    type: 'string',
                    label: 'String Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'stringProp'
                },
                numberProp: {
                    type: 'number',
                    label: 'Number Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'stringProp',
                },
                colorProp: {
                    type: 'color',
                    label: 'Color Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'colorProp'
                },
            },
        },
        {
            label: 'Object Control',
            id: 'testObjectControl2',
            name: 'testObjectControl2',
            type: 'object',
            // default: {},
            properties: {
                stringProp: {
                    type: 'string',
                    label: 'String Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'stringProp'
                },
                numberProp: {
                    type: 'number',
                    label: 'Number Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'stringProp',
                },
                colorProp: {
                    type: 'color',
                    label: 'Color Property',
                    helpText: 'Just as test.',
                    default: '',
                    id: 'colorProp'
                },
            },
        },
        {
            label: 'Inner Control 1',
            id: 'INNER_CONTROL1',
            name: 'INNER_CONTROL1',
            type: 'string'
        },
        {
            label: 'Inner Control 2',
            id: 'INNER_CONTROL2',
            name: 'INNER_CONTROL2',
            type: 'string'
        },
        {
            label: 'Calc Control Test',
            id: 'testCalcControl',
            name: 'testCalcControl',
            type: 'calccontrol',
            itemsSource: 'this.calcFields',
            itemsFilter: 'this.component.controls.testCalcFields.value.indexOf(this.item.text) !== -1',
            default: []
        },
        {
            id: 'pdfViewer1',
            name: 'pdfViewer1',
            type: 'pdfviewer',
            value: 'PDF_Succinctly_form_271483.pdf',
            disableFields: true
        },
        {
            id: 'pdfViewer',
            name: 'pdfViewer',
            type: 'pdfviewer',
            value: 'PDF_Succinctly_form.pdf'
        },
        {
            id: 'pdfEditor',
            name: 'pdfEditor',
            type: 'pdfviewer',
            value: 'PDF_Succinctly.pdf'
        },
        {
            label: 'Test Error Repeater',
            id: 'testErrorBtn',
            name: 'testErrorBtn',
            type: 'button',
            onclick: 'testErrorRepeater',
            default: []
        },
        {
            label: 'DD.COMMON.BUTTON_EDIT',
            id: 'ddTest',
            name: 'ddTest',
            type: 'button'
        },
        {
            label: 'DD.COMMON.BUTTON_FIND',
            id: 'ddTest1',
            name: 'ddTest1',
            type: 'string'
        },
        {
            label: 'Placeholder test',
            placeholder: 'DD.COMMON.BUTTON_APPLY_ALT',
            id: 'ddTest2',
            name: 'ddTest2',
            type: 'string'
        },
        {
            label: 'Items test',
            id: 'ddTest3',
            name: 'ddTest3',
            type: 'dropdown',
            items: [
                {
                    text: 'DD.COMMON.ENTITY_MODE_1',
                    value: 1
                },
                {
                    text: 'DD.COMMON.ENTITY_MODE_2',
                    value: 2
                },
                {
                    text: 'DD.COMMON.ENTITY_MODE_3',
                    value: 3
                }
            ]
        },
        {
            name: 'dialog1',
            label: 'Test Standard Dialog',
            id: 'dialog1',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'dialog2',
            label: 'Test Medium Dialog',
            id: 'dialog1',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'dialog3',
            label: 'Test Large Dialog',
            id: 'dialog1',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'dialog4',
            label: 'Test Full Width Dialog',
            id: 'dialog1',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'dialog5',
            label: 'Test With Response',
            id: 'dialog5',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'successDialog5',
            label: 'Test Systematic Close (success)',
            id: 'successDialog5',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'cancelDialog5',
            label: 'Test Systematic Close (cancel)',
            id: 'cancelDialog5',
            type: 'button',
            onclick: 'testDialog'
        },
        {
            name: 'testAsyncSub',
            label: 'Test Asynchronous Subscription',
            id: 'cancelDialog5',
            type: 'button',
            onclick: 'runTestAsyncSubscribe'
        },
        {
            name: 'progress1',
            type: "progress",
            pType: 'Circular',
            showValueLabel: true,
            value: 0
        },
        {
            name: 'progress2',
            type: "progress",
            pType: 'Linear',
            trackThickness: 24,
            progressThickness: 24,
            showValueLabel: true,
            value: 0
        },
        {
            name: 'startProgress',
            label: 'Run Progress Test',
            id: 'startProgress',
            type: 'button',
            onclick: 'runProgressTest'
        },
        {
            name: 'UPLOAD0',
            label: 'Single Upload With RecordId',
            type: 'upload',
            allowedExtensions: '.png, .jpg',
            entityName: 'Records',
            showRemoveConfirmation: false,
            showExtensionsText: true,
        },
        {
            name: 'UPLOAD1',
            label: 'Multiple type1',
            type: 'upload',
            allowedExtensions: '.png, .jpg',
            showAsDefaultInput: false,
            entityName: 'Records',
            multiple: true,
            showRemoveConfirmation: false,
            showExtensionsText: true,
            files: [
                {
                    name: 'test1 uploaded file',
                    size: 1024,
                    type: '.jpg',
                }
            ]
        },
        {
            name: 'UPLOAD2',
            label: 'Multiple type2',
            type: 'upload',
            allowedExtensions: '.png, .jpg',
            showAsDefaultInput: true,
            multiple: true,
            showRemoveConfirmation: false
        }
    ],
    testModeIf: [
        {
            record: 'component',
            prop: 'testProperty',
            valueType: 'criteria',
            value: 'Changed label for Some String',
            criteria: "this.testMode == true"
        }
    ]
};

const featureTestBenchCmp = {
    name: 'featureTestBench',
    preInitControls: true,
    editorSettings: editorSettings,
    dataTypes: {
        logicTime: 'time'
    },
    data() {
        return {
            editorSettings: editorSettings,
            testProperty: 'testProperty value',
            selectedTabIndex: 0,
            selectedSettingsModel: 'displayedConfig',
            previewEditorEnabled: true,
            previewTestMode: false,
            showActiveDateTime: false,
            calcFields: [
                {
                    text: 'field 1',
                    value: 'this.calcValues.field1'
                },
                {
                    text: 'field 2',
                    value: 'this.calcValues.field2'
                },
                {
                    text: 'field 3',
                    value: 'this.calcValues.field3'
                },
                {
                    text: 'field 4',
                    value: 'this.calcValues.field4'
                },
            ],
            calcValues: {
                field1: 1,
                field2: 5,
                field3: -5,
                field4: 1.125
            },
            typeOfAction: '',
            predefinedParams: {},
            progress1Value: 0,
            progress2Value: 0,
            progressInterval: false,
            uploadValue0: '',
            uploadValue1: '',
            uploadValue2: '',
            testLogicControlValue: [],
            logicBoolean: false,
            logicNumber: 0,
            logicString: 'not test',
            logicArray: ['item a value'],
            logicObjectArray: [{
                text: 'item a',
                value: 'item a value'
            }],
            logicDate: new Date('2021-10-05T00:00:00'),
            logicTime: null,
            logicDropdown: [],
            logicDropdowntree: [],
            logicResult1: false,
            logicResult2: false,
            logicResult3: false,
            logicResult4: false,
            pdfValue: 'PDF_Succinctly.pdf',
            localVolId: 0,
            localOppId: 0,
            localOrgId: 0,
            localLbeId: 0,
            dialogResponse: { status: 'none' },
            dialogResponse2: false,
            showListControl: true,
            testObjectControlValue: undefined,
            testObjectControlValue2: {
                stringProp: 'Value here'
            },
            timeToResponse: false,
        };
    },
    components: {
        settingsEditor,
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
        "ejs-tab": defineAsyncComponent(() => import('@common/syncfusion/tab')),
        "e-tabitems": TabItemsDirective,
        "e-tabitem": TabItemDirective,
        "json-viewer": JsonViewer,
        "sam-tooltip": SamTooltip
    },
    computed: {
        typeOfActionText() {
            if (!this.typeOfAction) return '';
            let outputObj = {
                layoutProperty: 'Set Layout Property',
                getProfileFields: 'View Profile',
                updateProfileField: 'Update Profile Field',
                runBackgroundCheck: 'Run Background Check',
                runAutomation: 'Run Automation',
                logout: 'Logout',
                refPlace: 'Place/Refer into opp or shift',
                openEmail: 'Open Email',
                openSurvey: 'Open Survey',
                openWaiver: 'Open Waiver',
                openTraining: 'Open Training',
                openUrl: 'Open a URL',
                openTab: 'Open Tab'
            };
            return outputObj[this.typeOfAction];
        },
        calcResult() {
            return library.calcParser.call(this, this.controls.testCalcControl.value);
        },
        displayedConfig() {
            let output = this.config;
            delete output.cId;
            delete output.cTitle;
            delete output.cType;
            delete output.cTemplate;
            delete output.cDescription;
            delete output.isGlobal;
            delete output.fromDb;
            delete output.loaded;
            delete output.configLoaded;
            delete output.controls;
            delete output.modifiedInstances;
            return output;
        },
        testTypes() {
            let testTypeFields = ['someString', 'someNumber', 'someBoolean', 'someDate', 'someColor', 'someDropdown', 'someCheckbox', 'someRadio'];
            let configKeys = Object.keys(this.displayedConfig)
                .filter((key) => testTypeFields.indexOf(key) !== -1);
            return configKeys.map((key) => ({
                key,
                val: this.displayedConfig[key]
            }));
        },
        oppRefPlRecords() {
            let volId = this.volId;
            if (!volId) return false;
            let source = '2___0___Refer Place Test Dataset for featureTestBench';
            let parentRecords = { 1: [volId] };
            let response = this.recordStore.subscribe({
                source,
                parentRecords
            }, this.cId);
            return response; // Reactive
        },
        volAffRecords() {
            let volId = this.volId;
            if (!volId) return false;
            let source = 'sds_getAffiliationList';
            let parentRecords = { 1: [volId] };
            let response = this.recordStore.subscribe({
                source,
                params: { volId: this.volId }
            }, this.cId);
            return response; // Reactive
        }
    },
    methods: {
        validationFunc(wizard) {
            let result = false;
            if (wizard.selectedTabIndex === 0) { // NOSONAR
                result = true;
            } else if (wizard.selectedTabIndex === 1 && wizard.$refs && wizard.$refs['ref_buildInfo'] && wizard.$refs['ref_buildInfo'][0] && wizard.$refs['ref_buildInfo'][0].buildInfo.indexOf('8305')) { // NOSONAR
                result = true;
            } else if (wizard.selectedTabIndex === 2 && wizard.$refs && wizard.$refs['ref_buildInfo'] && wizard.$refs['ref_buildInfo'][0] && wizard.$refs['ref_buildInfo'][0].buildInfo.indexOf('8305')) { // NOSONAR
                result = true;
            }
            return result;
        },
        async testDialog(_event, control) {
            if (control.name === 'cancelDialog5' || control.name === 'successDialog5') {
                setTimeout(async () => {
                    let isSuccess = control.name === 'successDialog5';
                    await library.hideDialog('dialog5', isSuccess);
                    this.dialogResponse2 = 'DIALOG CLOSED SYSTEMATICALLY';
                }, 2000); // NOSONAR
            }
            let dialogObj = {
                title: 'Test Dialog',
                message: 'Test message.'
            };
            if (control.name === 'dialog2') dialogObj.size = 'medium';
            if (control.name === 'dialog3') dialogObj.size = 'large';
            if (control.name === 'dialog4') dialogObj.size = 'full';
            if (control.name === 'dialog5' || control.name === 'cancelDialog5' || control.name === 'successDialog5') {
                dialogObj.name = 'dialog5';
                let preSuccessFunction = () => new Promise((resolve) => {
                    resolve({
                        status: 'separateStatus1',
                        message: 'response from pre-success function'
                    });
                });
                let successFunction = () => new Promise((resolve) => {
                    resolve({
                        status: 'separateStatus2',
                        message: 'response from success function'
                    });
                });
                let cancelFunction = () => new Promise((resolve) => {
                    resolve({
                        status: 'separateStatus3',
                        message: 'response from cancel function'
                    });
                });
                this.dialogResponse = await library.showDialog(dialogObj.message, successFunction, cancelFunction, dialogObj, preSuccessFunction, false);
            } else library.showDialog(dialogObj.message, false, false, dialogObj);
        },
        async testAsyncItems() {
            return [
                {
                    value: '01',
                    text: 'Music',
                    selectable: false,
                    htmlAttributes: { 'prevent-select': 'true' },
                    child: [
                        {
                            value: '01-01',
                            text: 'Gouttes.mp3'
                        }
                    ]
                },
                {
                    value: '02',
                    text: 'Videos',
                    selectable: false,
                    htmlAttributes: { 'prevent-select': 'true' },
                    expanded: true,
                    child: [
                        {
                            value: '02-01',
                            selectable: false,
                            htmlAttributes: { 'prevent-select': 'true' },
                            text: 'Naturals.mp4'
                        },
                        {
                            value: '02-02',
                            text: 'Wild.mpeg'
                        }
                    ]
                }
            ];
        },
        toggleList() {
            this.showListControl = !this.showListControl;
            // eslint-disable-next-line no-console
            console.log(this);
            // eslint-disable-next-line no-console
            console.log(controlStore());
        },
        testErrorRepeater() {
            this.errorHandler.handle("Testing the repeater", { allowMultiple: true });
        },
        runProgressTest() {
            if (this.progressInterval) clearInterval(this.progressInterval);
            this.progress1Value = 0;
            this.progress2Value = 0;
            this.progressInterval = setInterval(() => {
                let max = 100;
                if (this.progress1Value !== max) this.progress1Value += 20;
                if (this.progress2Value !== max) this.progress2Value += 10;
                if (this.progress2Value === max) {
                    clearInterval(this.progressInterval);
                    this.progressInterval = false;
                }
            }, 2000); // NOSONAR
        },
        runTestAction(type, index) {
            if (!type && typeof index === 'undefined') {
                library.showToast('No action configured.');
                return;
            }
            let uniqueValue = moment()
                .format("YYYYMMDD_Hmmss");
            let typeParams = {
                layoutProperty: {},
                getProfileFields: {
                    psp: 'vol',
                    recordId: 202072,
                    fields: ['VOL:::VOL_FIRST_NAME', 'VOL:::VOL_LAST_NAME', 'VOL:::VOL_DOB', 'VOL:::VOL_AGE']
                },
                updateProfileField: {
                    psp: 'vol',
                    recordId: 202072,
                    field: ['VOL:::VOL_LAST_NAME'],
                    value: 'Some Random' + uniqueValue
                },
                runBackgroundCheck: {
                    recordId: 202072,
                    packageID: 35
                },
                runAutomation: {
                    psp: 'vol',
                    automationID: 1234,
                    recordId: 202072
                },
                refPlace: {
                    volIds: 96001,
                    oppIds: 11453,
                    slotIds: 901178
                },
                openEmail: {
                    psp: 'vol',
                    recordId: 202072
                },
                openSurvey: {
                    recordId: 202072,
                    oppID: 11453,
                    packageID: 726300
                },
                openWaiver: {
                    recordId: 202072,
                    oppID: 11453,
                    packageID: 726426
                },
                openTraining: {
                    recordId: 202072,
                    oppID: 11453,
                    packageID: '309_216336'
                },
                openUrl: {
                    url: 'https://www.samaritan.com',
                    urlParams: '?test=1',
                    target: 'blank'
                },
                logout: {},
                testAction: {},
                openTab: {
                    psp: 'vol',
                    gridId: false,
                    filterId: false
                },
            };
            let aConfig = type
                ? {
                    params: typeParams[type],
                    action: type
                }
                : this.displayedConfig.configuredActions[index].configuredAction;
            if (type) {
                for (let key in aConfig.params) {
                    if (key === 'length') continue;
                    if (!this.predefinedParams[key]) delete aConfig.params[key];
                }
            }
            if (Array.isArray(aConfig)) {
                let record = {};
                actionStore.runChainedActions(aConfig, record, this);
            } else if (aConfig.action) {
                let payload = library.recordSourceMapper(aConfig.action, aConfig.params, {}, this, true);
                actionStore[aConfig.action](payload);
            }
        },
        resetPredefinedParams() {
            this.predefinedParams = {};
        },
        onUpdate() {
            library.logicParser.call(this, this.testLogicControlValue);
        },
        runTestUpload() {
            this.uploadValue2.forEach((upload) => {
                upload.status = 'uploaded';
            });
        },
        setRelativeId(_evt, control, params) {
            this.$store.state[this.layoutStoreId][params] = control.value;
        },
        // eslint-disable-next-line consistent-return
        async runTestAsyncSubscribe() {
            let recordId = this.volId;
            // eslint-disable-next-line no-alert
            if (!recordId) return alert('Please select a context vol to run this test.');
            let subscription = this.recordStore.subscribe({
                source: 'sds_getProfileField',
                params: {
                    recordId,
                    recordPsp: 'vol',
                    fields: ['VOL_FIRST_NAME', 'VOL_LAST_NAME'],
                }
            }, this.cId);
            let perf1 = performance.now();
            await subscription.ready; // Awaiting .ready
            perf1 = performance.now() - perf1;
            this.timeToResponse = perf1 + ' milliseconds';
        }
    },
    mounted() {
        this.localVolId = this.volId;
        this.localOppId = this.oppId;
        this.localOrgId = this.orgId;
        this.localLbeId = this.lbeId;
    }
};
componentStore.initComponent(featureTestBenchCmp, featureTestBenchConfig);
export { featureTestBenchConfig, featureTestBenchCmp };
export default featureTestBenchCmp;
</script>

<style scoped>
pre {
    padding: 10px;
    background: #f0f0f0;
}

pre {
    white-space: pre-wrap;
    line-height: 2em;
}

.test-value-types {
    margin-top: 10px;
    list-style: none;
}

.test-value-types li {
    padding: 10px;
    background: #f0f0f0;
    border-bottom: 1px solid #bbb;
    margin-bottom: 2px;
}

.date-time-display div span {
    width: 200px;
    padding: 10px;
    background: #eee;
    border-bottom: 1px solid #ddd;
    margin-bottom: 3px;
    display: inline-block;
}

.date-time-display div span + span {
    padding: 5px 5px 3px 10px;
    width: 150px;
    background: white;
    display: inline-block;
}

.upload-img-container {
    margin-top: 5px;
}

.upload-img-item {
    width: 23.5%;
    display: inline-block;
    overflow: hidden;
    border-radius: 4px;
    margin-right: 2%;
}

.upload-img-item:nth-child(4) {
    margin-right: 0;
}

h3 {
    margin: 20px 0;
}

hr {
    margin: 20px 0 40px 0;
    border: 0 none;
    border-bottom: 2px solid #4374be;
}

.logic-test {
    display: flex;
}

.logic-test > div {
    flex: 1;
}

.logic-test-result p {
    background: #eee;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 10px;
    margin: 10px;
}

.dialog-test-buttons {
    display: flex;
    gap: 10px;
}

:deep(.custom-css-class) {
    background: pink;
}
</style>
